import { appConfig } from '../config';
const API_BASE = appConfig.apiBaseUrl;

export const makeAuthenticatedRequest = async (endpoint, options = {}) => {
  // Token immer frisch aus localStorage lesen — nie aus einer Closure-Kopie,
  // damit ein kurz zuvor vom AuthContext gespeichertes neues Token sofort gilt.
  const getToken = () => localStorage.getItem('hejvi_access_token');

  if (!getToken()) {
    window.location.href = '/login';
    throw new Error('No access token available');
  }

  const buildHeaders = (token) => {
    const h = { 'Authorization': `Bearer ${token}`, ...options.headers };
    if (options.body && options.body instanceof FormData) {
      delete h['Content-Type'];
    } else if (!h['Content-Type']) {
      h['Content-Type'] = 'application/json';
    }
    return h;
  };

  const doFetch = (token) => fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: buildHeaders(token)
  });

  try {
    // Frisches Token für ersten Versuch lesen
    let response = await doFetch(getToken());

    // Token abgelaufen → einmal Refresh versuchen
    if (response.status === 401) {
      const refreshTokenValue = localStorage.getItem('hejvi_refresh_token');
      if (refreshTokenValue) {
        try {
          const refreshRes = await fetch(`${API_BASE}/refresh`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${refreshTokenValue}`,
              'Content-Type': 'application/json'
            }
          });
          if (refreshRes.ok) {
            const refreshData = await refreshRes.json();
            const newToken = refreshData.access_token;
            // Neues Token speichern
            localStorage.setItem('hejvi_access_token', newToken);
            const expiryTime = Date.now() + (15 * 60 * 1000);
            localStorage.setItem('hejvi_token_expiry', expiryTime.toString());
            // Request mit neuem Token wiederholen
            response = await doFetch(newToken);
          } else {
            // Refresh fehlgeschlagen → Auth-Daten löschen und zum Login
            ['hejvi_access_token','hejvi_refresh_token','hejvi_token_expiry','hejvi_user'].forEach(k => localStorage.removeItem(k));
            window.location.href = '/login';
            throw new Error('Authentication failed');
          }
        } catch (refreshErr) {
          if (refreshErr.message === 'Authentication failed') throw refreshErr;
          // Netzwerkfehler beim Refresh
          ['hejvi_access_token','hejvi_refresh_token','hejvi_token_expiry','hejvi_user'].forEach(k => localStorage.removeItem(k));
          window.location.href = '/login';
          throw new Error('Authentication failed');
        }
      } else {
        // Kein Refresh-Token → zum Login
        ['hejvi_access_token','hejvi_refresh_token','hejvi_token_expiry','hejvi_user'].forEach(k => localStorage.removeItem(k));
        window.location.href = '/login';
        throw new Error('Authentication failed');
      }
    }

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('API request error:', error);
    throw error;
  }
};

export const getElementByHash = async (hashId) => {
  try {
    const response = await makeAuthenticatedRequest(`/element/${hashId}`, {
      method: 'GET'
    });
    return response;
  } catch (error) {
    console.error('Error fetching element:', error);
    throw error;
  }
};

export const getElementById = async (elementId) => {
  try {
    const possibleEndpoints = [
      `/element/id/${elementId}`,
      `/element/${elementId}`,
      `/elements/${elementId}`,
      `/element/by-id/${elementId}`
    ];
    
    for (const endpoint of possibleEndpoints) {
      try {
        const response = await makeAuthenticatedRequest(endpoint, {
          method: 'GET'
        });
        return response;
      } catch (endpointError) {
        continue;
      }
    }
    
    throw new Error(`No working endpoint found for element ID ${elementId}`);
  } catch (error) {
    console.error('Error fetching element by ID:', error);
    throw error;
  }
};

export const getCollectionByHash = async (hashId) => {
  try {
    const response = await makeAuthenticatedRequest(`/collection/${hashId}`, {
      method: 'GET'
    });
    return response;
  } catch (error) {
    console.error('Error fetching collection:', error);
    throw error;
  }
};

export const getContentByHash = async (hashId) => {
  try {
    try {
      const elementResponse = await getElementByHash(hashId);
      return { type: 'element', data: elementResponse };
    } catch (elementError) {
      console.log('Not an element, trying collection...');
      
      try {
        const collectionResponse = await getCollectionByHash(hashId);
        return { type: 'collection', data: collectionResponse };
      } catch (collectionError) {
        throw new Error(`Content not found: ${hashId}`);
      }
    }
  } catch (error) {
    console.error('Error fetching content by hash:', error);
    throw error;
  }
};

export const voiceSearch = async (audioFile, options = {}) => {
  try {
    console.log('Voice search request:', {
      fileSize: audioFile.size,
      fileType: audioFile.type,
      fileName: audioFile.name,
      options
    });

    const formData = new FormData();
    // --- NEU: Prompt Parameter anhängen ---
    // Das hilft Whisper, den Kontext zu verstehen und Halluzinationen zu vermeiden.
    if (options.prompt) {
        formData.append('prompt', options.prompt);
    }
    // --------------------------------------
    formData.append('audio', audioFile);

    const queryParams = new URLSearchParams();
    if (options.limit) queryParams.append('limit', options.limit);
    if (options.type) queryParams.append('type', options.type);
    if (options.language_id) queryParams.append('language_id', options.language_id);

    const endpoint = `/voice-search${queryParams.toString() ? '?' + queryParams.toString() : ''}`;

    // Nutzen von makeAuthenticatedRequest (das nun FormData korrekt behandelt)
    const result = await makeAuthenticatedRequest(endpoint, {
      method: 'POST',
      body: formData
    });

    console.log('Voice search result:', result);
    return result;
  } catch (error) {
    console.error('Voice search error:', error);
    throw error;
  }
};

export const setProgress = async (hashId, progress) => {
  try {
    const progressValue = Math.min(100, Math.max(0, Number(progress) || 0));
    
    const response = await makeAuthenticatedRequest('/set-progress', {
      method: 'POST',
      body: JSON.stringify({
        progress: String(progressValue),
        hash_id: hashId
      })
    });

    const result = await response.json(); // Hinweis: makeAuthenticatedRequest returned bereits JSON, hier evtl doppelt? 
    // Korrektur: makeAuthenticatedRequest gibt bereits das geparste JSON zurück.
    // Falls setProgress bisher .json() aufrief, muss das angepasst werden,
    // ABER: In deinem Originalcode gibt makeAuthenticatedRequest das JSON zurück.
    // Daher hier einfach:
    return response; 
  } catch (error) {
    // Fallback falls die API anders reagiert als erwartet
    console.error('Failed to set progress:', error);
    throw error;
  }
};

export const setChallenge = async ({ challenge_id, answer_id, correct_answer_id, help_used }) => {
  try {
    const payload = {
      challenge_id: Number(challenge_id),
      answer_id: String(answer_id),
      correct_answer_id: String(correct_answer_id),
      help_used: Number(help_used ? 1 : 0)
    };

    return await makeAuthenticatedRequest('/set-challenge', {
      method: 'POST',
      body: JSON.stringify(payload)
    });
  } catch (error) {
    console.error('Failed to set challenge:', error);
    return { status: 'error', error: error?.message || 'set-challenge failed' };
  }
};

// In apiService.js am Ende oder bei den Element-Funktionen einfügen:

export const getSuggestedElements = async (courseName, courseDescription) => {
  try {
    const response = await makeAuthenticatedRequest('/suggest-elements', {
      method: 'POST',
      body: JSON.stringify({
        name: courseName,
        description: courseDescription
      })
    });
    // Erwartet ein Array von IDs, z.B. [10, 45, 2]
    return response;
  } catch (error) {
    console.error('AI Suggestion failed:', error);
    return []; // Fallback: Leeres Array (keine Filterung)
  }
};


// ─── Dokument-Chat (RAG) ─────────────────────────────────────────────

/**
 * Stellt eine Frage an ein oder alle Dokumente via RAG.
 *
 * @param {string} question        - Die Frage des Nutzers
 * @param {number|null} elementId  - Bestimmtes Dokument, oder null für alle
 * @param {string} sessionId       - UUID für Gesprächsverlauf
 * @param {Array} history          - [ {role, content}, ... ]
 * @param {string} token           - Auth-Token
 */
export const documentChat = async (question, elementId, sessionId, history, token) => {
  const response = await fetch(`${API_BASE}/document-chat`, {
    method:  'POST',
    headers: {
      'Content-Type':  'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({
      question,
      session_id: sessionId,
      history:    history || [],
      ...(elementId != null && { element_id: elementId }),
    }),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `HTTP ${response.status}`);
  }

  return response.json();
  // Rückgabe:
  // { status: 'success', answer: string, sources: Array, is_rag: boolean }
};


// ─── Vektorisierungsstatus ───────────────────────────────────────────

/**
 * Gibt den Embedding-Status eines Elements zurück.
 * Nützlich, um im Frontend zu zeigen ob ein Dokument "Chat-bereit" ist.
 *
 * @param {number} elementId
 * @param {string} token
 */
export const getEmbeddingStatus = async (elementId, token) => {
  const response = await fetch(`${API_BASE}/embedding-status?element_id=${elementId}`, {
    headers: { 'Authorization': `Bearer ${token}` },
  });

  if (!response.ok) return { status: 'unknown' };
  return response.json();
  // Rückgabe: { status: 'pending'|'processing'|'done'|'failed', chunk_count: number }
};


// ─── Smarte Suche (Keyword ODER RAG) ────────────────────────────────

/**
 * Ersetzt die bisherige reine Keyword-Suche.
 * Das Backend entscheidet, ob es eine Suche oder RAG-Anfrage ist.
 *
 * @param {string} query
 * @param {string} token
 * @param {Object} options - { limit, type, language_id }
 */
export const smartSearch = async (query, token, options = {}) => {
  const params = new URLSearchParams({
    q:     query,
    limit: options.limit     || 20,
    ...(options.type        && { type:        options.type }),
    ...(options.language_id && { language_id: options.language_id }),
  });

  const response = await fetch(`${API_BASE}/smart-search?${params}`, {
    headers: { 'Authorization': `Bearer ${token}` },
  });

  if (!response.ok) {
    throw new Error(`Search failed: HTTP ${response.status}`);
  }

  return response.json();
  // Rückgabe enthält zusätzlich: { response_type: 'rag'|'search', ... }
};


export const generateScriptSuggestions = async (name, description, language) => {
  try {
    console.log ("Call for generate-script! " + name);
    const response = await makeAuthenticatedRequest('/generate-script-suggestions', {
      method: 'POST',
      body: JSON.stringify({
        name,
        description,
        language
      })
    });
    // Das Backend gibt { options: [...] } zurück
    return response;
  } catch (error) {
    console.error('Script suggestion failed:', error);
    throw error;
  }
};

/**
 * Fügt ein Element einer bestehenden Collection hinzu.
 * @param {string} collectionHash  - hash_id der Collection (z.B. "col-abc123")
 * @param {number} elementId       - numerische ID des Elements
 */
export const addElementToCollection = async (collectionHash, elementId) => {
  return await makeAuthenticatedRequest('/collection/add-element', {
    method: 'POST',
    body: JSON.stringify({
      collection_hash: collectionHash,
      element_id: elementId
    })
  });
};


// ─── Text-to-Speech (TTS) ────────────────────────────────────────────────────
//
// Konfiguration in config.js (appConfig):
//
//   ttsProvider: 'openai'    → OpenAI TTS (Stimme: echo, benötigt Backend)
//   ttsProvider: 'browser'   → Web Speech API des Browsers (kostenlos, offline)
//
// Konfiguration in settings.js (window.HEJVI_SETTINGS):
//
//   ttsProvider: 'browser',  // ← 'browser' oder 'openai' (Standard: 'openai')
//
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Erstellt einen einheitlichen Audio-Wrapper für Browser-SpeechSynthesis,
 * der dieselbe Schnittstelle wie HTMLAudioElement hat:
 * { play(), pause(), addEventListener('ended'|'error', cb) }
 */
const _browserTtsWrapper = (text, language) => {
  const callbacks = { ended: [], error: [] };

  const utterance = new SpeechSynthesisUtterance(text);
  utterance.lang  = language;  // z.B. 'de', 'en', 'fr' – Browser wählt passende Stimme
  utterance.rate  = 1.0;
  utterance.pitch = 1.0;

  utterance.onend   = () => callbacks.ended.forEach(cb => cb());
  utterance.onerror = (e) => {
    // 'interrupted' ist kein echter Fehler (passiert bei pause()/cancel())
    if (e.error !== 'interrupted') {
      callbacks.error.forEach(cb => cb(e));
    }
  };

  return {
    play() {
      // Falls gerade etwas läuft: zuerst stoppen
      if (window.speechSynthesis.speaking) {
        window.speechSynthesis.cancel();
      }
      window.speechSynthesis.speak(utterance);
    },
    pause() {
      window.speechSynthesis.cancel(); // cancel() statt pause() – pause() ist buggy in Chrome
    },
    addEventListener(event, cb) {
      if (callbacks[event]) callbacks[event].push(cb);
    },
  };
};

/**
 * Wandelt Text in Sprache um.
 *
 * Provider wird über appConfig.ttsProvider gesteuert:
 *   'browser' → Web Speech API (kostenlos, keine Serverkosten)
 *   'openai'  → OpenAI TTS via Backend (Stimme: echo)
 *
 * Rückgabe: einheitliches Objekt mit { play(), pause(), addEventListener() }
 *
 * @param {string} text      - Vorzulesender Text
 * @param {string} language  - ISO-639-1 Code, z.B. 'de', 'en', 'fr'
 */
export const ttsSpeak = async (text, language = 'de') => {
  if (!text || typeof text !== 'string' || text.trim() === '') {
    throw new Error('ttsSpeak: text darf nicht leer sein.');
  }

  const provider = (
    window.HEJVI_SETTINGS?.ttsProvider || appConfig.ttsProvider || 'openai'
  ).toLowerCase();

  // ── Browser Web Speech API ────────────────────────────────────────
  if (provider === 'browser') {
    if (!window.speechSynthesis) {
      throw new Error('Web Speech API wird von diesem Browser nicht unterstützt.');
    }
    return _browserTtsWrapper(text.trim(), language);
  }

  // ── OpenAI TTS (Standard) ─────────────────────────────────────────
  const result = await makeAuthenticatedRequest('/tts', {
    method: 'POST',
    body: JSON.stringify({ text: text.trim(), language }),
  });

  if (result.status !== 'success' || !result.audio_base64) {
    throw new Error(result.error || 'TTS: Keine Audiodaten erhalten.');
  }

  // Base64 → Blob → Object-URL → HTMLAudioElement
  const byteChars = atob(result.audio_base64);
  const byteNums  = new Uint8Array(byteChars.length);
  for (let i = 0; i < byteChars.length; i++) {
    byteNums[i] = byteChars.charCodeAt(i);
  }

  const blob  = new Blob([byteNums], { type: 'audio/mpeg' });
  const url   = URL.createObjectURL(blob);
  const audio = new Audio(url);

  // Object-URL automatisch freigeben wenn Wiedergabe beendet
  audio.addEventListener('ended', () => URL.revokeObjectURL(url), { once: true });

  return audio;
};
// ─── Admin API ────────────────────────────────────────────────────────────────

export const adminGetStats = () =>
  makeAuthenticatedRequest('/admin/stats');

export const adminListUsers = () =>
  makeAuthenticatedRequest('/admin/users');

export const adminCreateUser = (data) =>
  makeAuthenticatedRequest('/admin/users/create', { method: 'POST', body: JSON.stringify(data) });

export const adminUpdateUser = (data) =>
  makeAuthenticatedRequest('/admin/users/update', { method: 'POST', body: JSON.stringify(data) });

export const adminDeleteUser = (id) =>
  makeAuthenticatedRequest('/admin/users/delete', { method: 'POST', body: JSON.stringify({ id }) });

export const adminListUsergroups = () =>
  makeAuthenticatedRequest('/admin/usergroups');

export const adminCreateUsergroup = (data) =>
  makeAuthenticatedRequest('/admin/usergroups/create', { method: 'POST', body: JSON.stringify(data) });

export const adminUpdateUsergroup = (data) =>
  makeAuthenticatedRequest('/admin/usergroups/update', { method: 'POST', body: JSON.stringify(data) });

export const adminDeleteUsergroup = (id) =>
  makeAuthenticatedRequest('/admin/usergroups/delete', { method: 'POST', body: JSON.stringify({ id }) });

export const adminAssignUserToGroup = (user_id, group_id) =>
  makeAuthenticatedRequest('/admin/usergroups/assign-user', { method: 'POST', body: JSON.stringify({ user_id, group_id }) });

export const adminRemoveUserFromGroup = (user_id, group_id) =>
  makeAuthenticatedRequest('/admin/usergroups/remove-user', { method: 'POST', body: JSON.stringify({ user_id, group_id }) });

export const adminListElements = () =>
  makeAuthenticatedRequest('/admin/elements');

export const adminUpdateElement = (data) =>
  makeAuthenticatedRequest('/admin/elements/update', { method: 'POST', body: JSON.stringify(data) });

export const adminDeleteElement = (id) =>
  makeAuthenticatedRequest('/admin/elements/delete', { method: 'POST', body: JSON.stringify({ id }) });

export const adminListCollections = () =>
  makeAuthenticatedRequest('/admin/collections');

export const adminUpdateCollection = (data) =>
  makeAuthenticatedRequest('/admin/collections/update', { method: 'POST', body: JSON.stringify(data) });

export const adminDeleteCollection = (id) =>
  makeAuthenticatedRequest('/admin/collections/delete', { method: 'POST', body: JSON.stringify({ id }) });

export const adminAssignContentToGroup = (element_id, group_id, element_type = 1, order = 10) =>
  makeAuthenticatedRequest('/admin/content/assign-group', { method: 'POST', body: JSON.stringify({ element_id, group_id, element_type, order }) });

export const adminRemoveContentFromGroup = (element_id, group_id, element_type = 1) =>
  makeAuthenticatedRequest('/admin/content/remove-group', { method: 'POST', body: JSON.stringify({ element_id, group_id, element_type }) });

// ─── fetchWithAuth — FormData-Upload mit Token-Refresh ───────────────────────
//
// Für Uploads (ElementCreator, CourseBuilder, QuizBuilder), die FormData senden
// und daher nicht durch makeAuthenticatedRequest laufen können.
// Bei 401 wird einmal ein Token-Refresh versucht; schlägt der fehl → /login.
//
export const fetchWithAuth = async (url, options = {}) => {
  const getToken = () => localStorage.getItem('hejvi_access_token');
  const clearAuth = () =>
    ['hejvi_access_token','hejvi_refresh_token','hejvi_token_expiry','hejvi_user']
      .forEach(k => localStorage.removeItem(k));

  const buildHeaders = (token, extra = {}) => ({
    'Authorization': `Bearer ${token}`,
    ...extra,
  });

  let token = getToken();
  if (!token) { clearAuth(); window.location.href = '/login'; throw new Error('Authentication failed'); }

  let response = await fetch(url, { ...options, headers: buildHeaders(token, options.headers) });

  if (response.status === 401) {
    const refreshTok = localStorage.getItem('hejvi_refresh_token');
    if (!refreshTok) { clearAuth(); window.location.href = '/login'; throw new Error('Authentication failed'); }

    try {
      const refreshRes = await fetch(`${API_BASE}/refresh`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${refreshTok}`, 'Content-Type': 'application/json' },
      });
      if (!refreshRes.ok) { clearAuth(); window.location.href = '/login'; throw new Error('Authentication failed'); }

      const refreshData = await refreshRes.json();
      token = refreshData.access_token;
      localStorage.setItem('hejvi_access_token', token);
      localStorage.setItem('hejvi_token_expiry', String(Date.now() + 15 * 60 * 1000));

      // Request mit neuem Token wiederholen
      response = await fetch(url, { ...options, headers: buildHeaders(token, options.headers) });
    } catch (e) {
      if (e.message === 'Authentication failed') throw e;
      throw new Error('Authentication failed');
    }
  }

  return response; // rohe Response zurückgeben — Caller parst .json() selbst
};
